"use client";

import { useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 15 * 60 * 1000;
const VERSION_URL = "/version.json";

interface VersionPayload {
  readonly sha: string;
}

async function fetchSha(): Promise<string | null> {
  try {
    const res = await fetch(VERSION_URL, { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as VersionPayload;
    return typeof data.sha === "string" && data.sha.length > 0
      ? data.sha
      : null;
  } catch {
    return null;
  }
}

export function useVersionCheck(enabled: boolean): {
  readonly updateAvailable: boolean;
} {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialShaRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setUpdateAvailable(false);
      initialShaRef.current = null;
      return;
    }

    let cancelled = false;

    async function check(): Promise<void> {
      const sha = await fetchSha();
      if (cancelled || sha === null) return;
      if (initialShaRef.current === null) {
        initialShaRef.current = sha;
        return;
      }
      if (sha !== initialShaRef.current) setUpdateAvailable(true);
    }

    void check();

    function onVisibilityChange(): void {
      if (document.visibilityState === "visible") void check();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    const intervalId = window.setInterval(() => {
      void check();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [enabled]);

  return { updateAvailable };
}
